/**
 * ========================================
 * CUSTOM ASSET - grid-image-swap-modded.js
 * Version: 1.1
 * Created: 2026-10-02
 * Last Modified: 2026-10-11
 * Client: Studio Optix
 * ========================================
 *
 * PURPOSE:
 * <image-swap-modded> custom element for the "Grid modded" section's image
 * cells (_grid-image-modded.liquid). Hover swap is pure CSS; this element
 * only handles touch devices (no hover capability):
 * - Tap toggles the .is-swapped class (crossfade to the rollover image).
 * - If the cell is a link, the FIRST tap swaps (navigation prevented) and
 *   the SECOND tap follows the link. Tapping a different cell resets the
 *   previously swapped one.
 * - Cells without a rollover image ([data-has-swap] absent) are untouched.
 *
 * MODIFICATIONS:
 * - v1.1 (2026-10-02, re-applied 2026-10-11 after the V0.1.10 core update
 *   shipped with the stale v1.0 copy): Ignore clicks on the content
 *   overlay (buttons and text links inside .grid-cell-content must act
 *   normally, not trigger the swap).
 * - v1.0 (2026-10-02): Initial creation.
 */

const touchOnly = window.matchMedia('(hover: none)');

class ImageSwapModded extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    if (!this.hasAttribute('data-has-swap')) return;

    this.addEventListener('click', this.#handleClick, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #handleClick = (event) => {
    if (!touchOnly.matches) return;

    // Overlay content (button, text links) acts normally — no swap logic.
    if (event.target instanceof Element && event.target.closest('.grid-cell-content')) return;

    const isLink = Boolean(this.querySelector('a.grid-swap'));

    if (this.classList.contains('is-swapped')) {
      // Second tap: links navigate; plain images toggle back.
      if (!isLink) this.classList.remove('is-swapped');
      return;
    }

    if (isLink) event.preventDefault();

    // Reset any other swapped cell so only one is active at a time.
    for (const other of document.querySelectorAll('image-swap-modded.is-swapped')) {
      if (other !== this) other.classList.remove('is-swapped');
    }

    this.classList.add('is-swapped');
  };
}

if (!customElements.get('image-swap-modded')) {
  customElements.define('image-swap-modded', ImageSwapModded);
}
